import {
  Container,
  Paper,
  Text,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Stack,
  Badge,
} from "@mantine/core";
import { IconPlus, IconSearch, IconEye, IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTenders } from "../hooks/useTenders";
import { StageBadge } from "../components/StageBadge";
import dayjs from "dayjs";

export function TenderList() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: "",
    stage: "" as
      | "PUBLISHED"
      | "COLLECTION"
      | "OPENED"
      | "TECH_EVAL"
      | "FIN_EVAL"
      | "AWARDED"
      | "EXPIRED"
      | "",
    category: "" as "GOODS" | "SERVICES" | "WORKS" | "",
  });

  const { data: tenders, isLoading } = useTenders({
    search: filters.search,
    stage: filters.stage || undefined,
    category:
      (filters.category as "GOODS" | "SERVICES" | "WORKS" | undefined) ||
      undefined,
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading tenders...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Tenders</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate("/tenders/new")}
        >
          New Tender
        </Button>
      </Group>

      {/* Filters */}
      <Paper shadow="sm" p="md" mb="xl">
        <Group gap="md">
          <TextInput
            placeholder="Search tenders..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Stage"
            data={[
              { value: "", label: "All Stages" },
              { value: "PUBLISHED", label: "Published" },
              { value: "COLLECTION", label: "Collection" },
              { value: "OPENED", label: "Opened" },
              { value: "TECH_EVAL", label: "Technical Evaluation" },
              { value: "FIN_EVAL", label: "Financial Evaluation" },
              { value: "AWARDED", label: "Awarded" },
              { value: "EXPIRED", label: "Expired" },
            ]}
            value={filters.stage}
            onChange={(value) => handleFilterChange("stage", value || "")}
            style={{ minWidth: 200 }}
          />
          <Select
            placeholder="Category"
            data={[
              { value: "", label: "All Categories" },
              { value: "GOODS", label: "Goods" },
              { value: "SERVICES", label: "Services" },
              { value: "WORKS", label: "Works" },
            ]}
            value={filters.category}
            onChange={(value) => handleFilterChange("category", value || "")}
            style={{ minWidth: 150 }}
          />
        </Group>
      </Paper>

      {/* Tenders Table */}
      <Paper shadow="sm">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Reference</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Stage</Table.Th>
              <Table.Th>Deadline</Table.Th>
              <Table.Th>Bidders</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tenders?.map((tender) => (
              <Table.Tr key={tender.id}>
                <Table.Td>
                  <Text fw={500}>{tender.referenceNo}</Text>
                </Table.Td>
                <Table.Td>
                  <Text lineClamp={2} style={{ maxWidth: 300 }}>
                    {tender.description}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light">{tender.category}</Badge>
                </Table.Td>
                <Table.Td>
                  <StageBadge stage={tender.stage} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {dayjs(tender.submissionDeadline).format("MMM DD, YYYY HH:mm")}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{tender.bidders.length} bidders</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      onClick={() => navigate(`/tenders/${tender.id}`)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="light"
                      onClick={() => navigate(`/tenders/${tender.id}/edit`)}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {tenders?.length === 0 && (
        <Paper shadow="sm" p="xl" mt="md">
          <Stack align="center" gap="md">
            <Text c="dimmed">No tenders found</Text>
            <Button variant="light" onClick={() => navigate("/tenders/new")}>
              Create your first tender
            </Button>
          </Stack>
        </Paper>
      )}
    </Container>
  );
}
